import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LIMITED_STAFF_NAV } from '../../portalMode';
import {
  LayoutDashboard,
  BookMarked,
  Users,
  Briefcase,
  FileText,
  DollarSign,
  FolderOpen,
  Bell,
  Handshake,
  Shield,
  Receipt,
  MessageSquare,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  ArrowLeft,
  Menu,
  Building2,
  Upload,
  CreditCard,
  CalendarDays,
  Wallet,
  ArrowDownCircle,
  Receipt as ReceiptIcon,
  Layers,
  FileSearch,
  BookOpen,
  FolderTree,
  ClipboardList,
  Image as ImageIcon,
  ShieldCheck,
  TrendingUp,
  ArrowUpCircle,
  ArrowLeftRight,
  KeyRound,
  Repeat,
  Lock,
  ChevronDown,
  Database,
  Building,
  Gem,
} from 'lucide-react';
import { Button } from '../ui/button';

// Sidebar structure only — every `to` is unchanged from the old flat list,
// so nothing else in the app (notification deep-links, breadcrumbs, direct
// URLs) breaks. Grouping/order is purely presentational.
const ADMIN_NAV = [
  { type: 'link', to: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  {
    type: 'group',
    key: 'investors',
    label: 'Investors',
    icon: Users,
    to: '/admin/investors',
    items: [
      { to: '/admin/investors', icon: Users, label: 'Investors' },
      { to: '/admin/contribution-requests', icon: ArrowUpCircle, label: 'Contributions' },
      { to: '/admin/investor-pricing', icon: CalendarDays, label: 'Investor Pricing' },
      { to: '/admin/transactions', icon: ReceiptIcon, label: 'Transactions' },
      { to: '/admin/withdrawal-requests', icon: ArrowDownCircle, label: 'Withdrawals' },
      { to: '/admin/marketplace', icon: ArrowLeftRight, label: 'Marketplace & Carry' },
      { to: '/admin/deals', icon: Handshake, label: 'Deals' },
      { to: '/admin/deal-room', icon: Gem, label: 'Deal Room' },
      { to: '/admin/distributions', icon: DollarSign, label: 'Distributions' },
      { to: '/admin/portfolio-metrics', icon: TrendingUp, label: 'Portfolio (IR)' },
    ],
  },
  {
    type: 'group',
    key: 'clients',
    label: 'Clients',
    icon: Building2,
    to: '/admin/clients',
    items: [
      { to: '/admin/clients', icon: Building2, label: 'Clients' },
      { to: '/admin/fund-requests', icon: CreditCard, label: 'Fund Requests' },
      { to: '/admin/loans', icon: Briefcase, label: 'Loans' },
      { to: '/admin/monthly-submissions', icon: CalendarDays, label: 'Monthly Docs' },
    ],
  },
  { type: 'link', to: '/admin/ledger', icon: BookOpen, label: 'General Ledger' },
  { type: 'link', to: '/admin/tax', icon: Receipt, label: 'Tax Certificates' },
  { type: 'link', to: '/admin/notices', icon: Bell, label: 'Notices' },
  {
    type: 'group',
    key: 'data',
    label: 'Data',
    icon: Database,
    to: null,
    items: [
      { to: '/admin/investec', icon: Wallet, label: 'Investec Bank' },
      { to: '/admin/import', icon: Upload, label: 'Data Import' },
    ],
  },
  {
    type: 'group',
    key: 'internal',
    label: 'Internal',
    icon: Building,
    to: null,
    items: [
      { to: '/admin/documents', icon: FolderOpen, label: 'Documents' },
      { to: '/admin/media', icon: ImageIcon, label: 'Media Library' },
      { to: '/admin/risk-tranches', icon: Layers, label: 'Risk Tranches' },
      { to: '/admin/sectors', icon: FolderTree, label: 'Sector Classification' },
      { to: '/admin/statements', icon: FileText, label: 'Statements' },
      { to: '/admin/kyc-compliance', icon: ShieldCheck, label: 'KYC Compliance' },
      { to: '/admin/kyc-review', icon: Shield, label: 'KYC Review' },
      { to: '/admin/internal-transfers', icon: ArrowLeftRight, label: 'Transfers' },
      { to: '/admin/manuals', icon: BookMarked, label: 'User Manuals' },
    ],
  },
  {
    type: 'group',
    key: 'administration',
    label: 'Administration',
    icon: Lock,
    to: null,
    items: [
      { to: '/admin/api-keys', icon: KeyRound, label: 'API Keys' },
      { to: '/admin/audit-trail', icon: ClipboardList, label: 'Audit Trail' },
      { to: '/admin/company-settings', icon: Building2, label: 'Company Settings' },
      { to: '/admin/ocr', icon: FileSearch, label: 'Document OCR' },
      { to: '/admin/support', icon: MessageSquare, label: 'Support Tickets' },
      { to: '/admin/settings', icon: Settings, label: 'System Settings' },
      { to: '/admin/templates', icon: FileText, label: 'Templates' },
      { to: '/admin/user-management', icon: Shield, label: 'User Management' },
    ],
  },
];

const isItemActive = (item, pathname) =>
  item.exact ? pathname === item.to : pathname.startsWith(item.to);

const AdminSidebar = ({ mobileOpen, onMobileClose }) => {
  const { user, logout, getLinkedAccounts, switchAccount } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  // Analysts get the admin portal but not the "Administration" group
  // (API keys, audit trail, company/system settings, templates, user
  // management, ...) — the backend already enforces this (require_admin
  // on every one of those endpoints), this just keeps the menu honest.
  // Compliance / IR (admin build) see only their own screens.
  const limited = LIMITED_STAFF_NAV[user?.role];
  const visibleNav = limited
    ? ADMIN_NAV.flatMap(entry => entry.type === 'group' ? entry.items : [entry]).filter(i => limited.includes(i.to)).map(i => ({ ...i, type: 'link' }))
    : user?.role === 'admin'
      ? ADMIN_NAV
      : ADMIN_NAV.filter(entry => entry.key !== 'administration');
  const visibleFlatNav = visibleNav.flatMap(entry => entry.type === 'group' ? entry.items : [entry]);

  // Which nav groups are expanded. Whatever group contains the current
  // route starts open; anything the admin manually opens afterward stays
  // open across navigation (only auto-opens, never auto-closes).
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initial = {};
    for (const entry of visibleNav) {
      if (entry.type !== 'group') continue;
      const isActiveGroup = entry.items.some(i => isItemActive(i, location.pathname)) ||
        (entry.to && location.pathname === entry.to);
      if (isActiveGroup) initial[entry.key] = true;
    }
    return initial;
  });

  useEffect(() => {
    const activeGroup = visibleNav.find(entry =>
      entry.type === 'group' &&
      (entry.items.some(i => isItemActive(i, location.pathname)) || (entry.to && location.pathname === entry.to))
    );
    if (activeGroup) {
      setExpandedGroups(prev => (prev[activeGroup.key] ? prev : { ...prev, [activeGroup.key]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // This admin's own investor/client accounts (same email, a genuine
  // sibling record — see /auth/linked-accounts), NOT an arbitrary investor
  // picked via the admin's "Impersonate" feature. Lets an admin who is
  // also a real investor/client jump straight to their own portal instead
  // of impersonating themselves or using the data-less ?preview=1 mode.
  const [myPortalAccounts, setMyPortalAccounts] = useState([]);
  const [switchingId, setSwitchingId] = useState(null);

  useEffect(() => {
    getLinkedAccounts()
      .then(accounts => {
        // One entry per role (investor/client), not one per matching
        // account — if this admin has several linked investor accounts,
        // the link just needs to get them INTO the investor portal; the
        // account-switcher dropdown in there (Header.js) covers hopping
        // between any siblings from there.
        const seenRoles = new Set();
        const deduped = accounts
          .filter(a => ['investor', 'client'].includes(a.role))
          .filter(a => (seenRoles.has(a.role) ? false : (seenRoles.add(a.role), true)));
        setMyPortalAccounts(deduped);
      })
      .catch(() => setMyPortalAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBackToPortal = () => {
    // Fallback for admins with no linked investor/client account of their
    // own — a UI-only preview under the admin's own (data-less) session.
    navigate('/dashboard?preview=1');
  };

  const handleSwitchToMyPortal = async (account) => {
    setSwitchingId(account.id);
    try {
      const switchedUser = await switchAccount(account.id);
      window.location.href = switchedUser?.role === 'client' ? '/client' : '/dashboard';
    } catch (error) {
      console.error('Failed to switch to linked portal account:', error);
      setSwitchingId(null);
    }
  };

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
          data-testid="admin-sidebar-backdrop"
        />
      )}
      <aside
        className={`fixed left-0 top-0 h-screen bg-[#0F172A] border-r border-slate-700 transition-all duration-300 z-50 flex flex-col w-64 ${
          collapsed ? 'lg:w-16' : 'lg:w-64'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        data-testid="admin-sidebar"
      >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <img 
              src="/logo-tree.svg" 
              alt="Plane Tree Capital" 
              className="w-8 h-8"
            />
            <div>
              <h1 className="text-sm font-bold text-white">Admin Portal</h1>
              <p className="text-[10px] text-slate-400">Plane Tree Capital</p>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:inline-flex text-slate-400 hover:text-white hover:bg-slate-700"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
        {/* Mobile/tablet close button — the desktop collapse toggle above is
            hidden below lg, so the drawer needs its own explicit close. */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMobileClose}
          className="lg:hidden text-slate-400 hover:text-white hover:bg-slate-700"
          data-testid="admin-sidebar-close"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>

      {/* My Portal Accounts — only shown when this admin's own email also
          has a real investor and/or client account (a genuine sibling
          record, not impersonation). Switches straight into it with real
          data via /auth/switch-account. */}
      {myPortalAccounts.length > 0 && (
        <div className="p-2 border-b border-slate-700 space-y-1">
          {myPortalAccounts.map((account) => (
            <Button
              key={account.id}
              variant="ghost"
              onClick={() => handleSwitchToMyPortal(account)}
              disabled={switchingId === account.id}
              className={`w-full justify-start text-emerald-400 hover:text-emerald-300 hover:bg-slate-800 ${
                collapsed ? 'px-2' : ''
              }`}
              data-testid={`my-portal-link-${account.role}`}
              title={collapsed ? `My ${account.role} portal` : undefined}
            >
              <Repeat className="h-4 w-4 flex-shrink-0" />
              {!collapsed && (
                <span className="ml-2 text-sm capitalize">My {account.role} portal</span>
              )}
            </Button>
          ))}
        </div>
      )}

      {/* Back to Portal — fallback preview for admins with no linked
          investor/client account of their own. */}
      <div className="p-2 border-b border-slate-700">
        <Button
          variant="ghost"
          onClick={handleBackToPortal}
          className={`w-full justify-start text-amber-400 hover:text-amber-300 hover:bg-slate-800 ${
            collapsed ? 'px-2' : ''
          }`}
          data-testid="back-to-portal-btn"
        >
          <ArrowLeft className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span className="ml-2 text-sm">Back to Portal</span>}
        </Button>
      </div>

      {/* Navigation — clicking any leaf link closes the mobile drawer.
          The group-header chevron stops propagation itself (see its own
          onClick below), so expanding/collapsing a group doesn't close it. */}
      <nav
        className="flex-1 overflow-y-auto py-4"
        onClick={(e) => { if (e.target.closest('a')) onMobileClose?.(); }}
      >
        <ul className="space-y-1 px-2">
          {collapsed ? (
            // Icon rail — no room for group headers/indentation, fall back
            // to one flat list of every leaf link.
            visibleFlatNav.map((item) => {
              const isActive = isItemActive(item, location.pathname);
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-amber-500 text-slate-900 font-medium'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                    title={item.label}
                    data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <item.icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-slate-900' : ''}`} />
                  </NavLink>
                </li>
              );
            })
          ) : (
            visibleNav.map((entry) => {
              if (entry.type === 'link') {
                const isActive = isItemActive(entry, location.pathname);
                return (
                  <li key={entry.to}>
                    <NavLink
                      to={entry.to}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                        isActive
                          ? 'bg-amber-500 text-slate-900 font-medium'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`}
                      data-testid={`admin-nav-${entry.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <entry.icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-slate-900' : ''}`} />
                      <span className="text-sm">{entry.label}</span>
                    </NavLink>
                  </li>
                );
              }

              // Group — "Investors"/"Clients" are also real pages (their
              // header row navigates); "Data"/"Internal"/"Administration"
              // have no page of their own (their header row only toggles).
              const isExpanded = !!expandedGroups[entry.key];
              const headerIsActivePage = entry.to && location.pathname === entry.to;
              const HeaderIcon = entry.icon;

              const headerInner = (
                <>
                  {HeaderIcon ? (
                    <HeaderIcon className={`h-5 w-5 flex-shrink-0 ${headerIsActivePage ? 'text-slate-900' : ''}`} />
                  ) : (
                    <span className="w-5 flex-shrink-0" aria-hidden="true" />
                  )}
                  <span className="text-sm flex-1 text-left">{entry.label}</span>
                  <ChevronDown
                    className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleGroup(entry.key);
                    }}
                    data-testid={`admin-nav-group-toggle-${entry.key}`}
                  />
                </>
              );

              return (
                <li key={entry.key}>
                  {entry.to ? (
                    <NavLink
                      to={entry.to}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                        headerIsActivePage
                          ? 'bg-amber-500 text-slate-900 font-medium'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`}
                      data-testid={`admin-nav-group-${entry.key}`}
                    >
                      {headerInner}
                    </NavLink>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleGroup(entry.key)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-slate-300 hover:bg-slate-800 hover:text-white"
                      data-testid={`admin-nav-group-${entry.key}`}
                    >
                      {headerInner}
                    </button>
                  )}

                  {isExpanded && (
                    <ul className="mt-1 mb-1 ml-5 pl-3 space-y-1 border-l border-slate-700">
                      {entry.items.map((item) => {
                        const isActive = isItemActive(item, location.pathname);
                        return (
                          <li key={item.to}>
                            <NavLink
                              to={item.to}
                              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                                isActive
                                  ? 'bg-amber-500 text-slate-900 font-medium'
                                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                              }`}
                              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                            >
                              <item.icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-slate-900' : ''}`} />
                              <span className="text-sm">{item.label}</span>
                            </NavLink>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })
          )}
        </ul>
      </nav>

      {/* Footer - User Info */}
      <div className="border-t border-slate-700 p-3">
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
          <div className="w-9 h-9 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-slate-900 font-bold text-sm">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          onClick={handleLogout}
          className={`mt-3 w-full text-red-400 hover:text-red-300 hover:bg-slate-800 ${
            collapsed ? 'justify-center px-2' : 'justify-start'
          }`}
          data-testid="admin-logout-btn"
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && <span className="ml-2 text-sm">Sign Out</span>}
        </Button>
      </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
